import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import { ICreateAbsenceRequestDTO, IValidateAbsenceErrorsDTO, IValidateErrorDTO } from 'src/api/dtos';
import AbsenceRepository from 'src/api/repositories/ess/v1/absence.repository';
import { showConfirmationToast } from 'src/shared/components';
import { TranslationFunction, useTranslate } from 'src/shared/providers';
import { isOfType, mapCreateAbsenceRequest } from 'src/shared/utils';

interface IAbsenceRequestValidationProps {
  inputs: Partial<ICreateAbsenceRequestDTO>;
}

export const useAbsenceRequestValidation = ({ inputs }: IAbsenceRequestValidationProps) => {
  const { t } = useTranslate();

  const [hasErrors, setHasErrors] = useState<boolean>(false);

  const resetErrors = () => {
    setHasErrors(false);
  };

  const validate = useCallback(async () => {
    try {
      toast.dismiss();
      setHasErrors(false);

      const results = await validateFields(inputs, t);

      if (!results?.length) {
        return;
      }

      showConfirmationToast({
        type: toast.TYPE.WARNING,
        title: t('absences.pleaseKeepInMind'),
        messages: [results[0].errorMessage],
        currentKey: results[0].errorCode,
        showCancelButton: false,
      });
      setHasErrors(true);
    } catch (e) {
      console.error(e);
    }
  }, [inputs]);

  return {
    validate,
    hasErrors,
    resetErrors,
  };
};

const validateFields = async (
  data: Partial<ICreateAbsenceRequestDTO>,
  t: TranslationFunction,
): Promise<IValidateErrorDTO[] | undefined> => {
  try {
    const params = mapCreateAbsenceRequest(data);

    const repository = new AbsenceRepository();
    await repository.validate(params);

    return [];
  } catch (e: any) {
    const { data } = e;

    if (isOfType<IValidateAbsenceErrorsDTO>(data, 'errors')) {
      return data.errors;
    }

    toast.info(t('absences.oopsAnErrorOccurredWhileValidatingYourInputs'), {
      type: toast.TYPE.ERROR,
    });
  }
};
